import { INITIALIZE_STORE } from "../actions/manager.actions";
import {
    NOTES_ADD_ATTRIBUTE,
    NOTES_ADD_NOTE,
    NOTES_ADD_TAG,
    NOTES_REMOVE_ATTRIBUTE,
    NOTES_REMOVE_NOTE,
    NOTES_REMOVE_TAG,
    NOTES_REPLACE_ATTRIBUTE,
    NOTES_REPLACE_NOTE,
    NOTES_SET_ATTRIBUTES,
    NOTES_SET_LOADING,
    NOTES_SET_NOTES,
    NOTES_SET_TAGS,
    NOTES_SET_VISUALIZER_DEFAULT,
} from "../actions/notes.actions";
import type { VisualizerConfig } from "@mappers/notes.mapper.mjs";

export interface NoteUserRef {
    uuid: string;
    name: string;
}

export interface NoteAttributeValue {
    id: number;
    value: number;
}

export type NoteVisibility = "open" | "targeted";

export interface Note {
    uuid: string;
    body: string;
    created_at: string;
    updated_at: string;
    // null means a team note.
    player: { id: number; name: string } | null;
    author: NoteUserRef | null;
    tags: string[];
    attributes: NoteAttributeValue[];
    visibility: NoteVisibility;
    audience: NoteUserRef[];
    can_edit: boolean;
    can_delete: boolean;
}

export interface NoteTag {
    id: string;
    name: string;
    // The notes the user can see. The filters use it.
    count: number;
    // All the notes of the team. A tag can be deleted only when it is 0.
    usage_count: number;
    created_by: NoteUserRef | null;
    created_at: string;
}

export interface NoteAttribute {
    id: number;
    name: string;
    min_value: number | null;
    max_value: number | null;
    usage_count: number;
    created_by: NoteUserRef | null;
    created_at: string;
}

export type NotesLoadingKey = "notes" | "tags" | "attributes";

export interface NotesState {
    notes: Note[];
    tags: NoteTag[];
    attributes: NoteAttribute[];
    loading: Record<NotesLoadingKey, boolean>;
    /** The team default of the Table View. Null until it loads. */
    visualizerDefault: VisualizerConfig | null;
}

const initialState: NotesState = {
    notes: [],
    tags: [],
    attributes: [],
    loading: { notes: false, tags: false, attributes: false },
    visualizerDefault: null,
};

export const notesReducer = (state = initialState, action: any): NotesState => {
    switch (action.type) {
        case NOTES_SET_LOADING:
            return { ...state, loading: { ...state.loading, [action.payload.key]: action.payload.value } };
        case NOTES_SET_NOTES:
            return { ...state, notes: action.payload };
        case NOTES_ADD_NOTE:
            return { ...state, notes: [action.payload, ...state.notes] };
        case NOTES_REPLACE_NOTE:
            return { ...state, notes: state.notes.map((n) => (n.uuid === action.payload.uuid ? action.payload : n)) };
        case NOTES_REMOVE_NOTE:
            return { ...state, notes: state.notes.filter((n) => n.uuid !== action.payload) };
        case NOTES_SET_VISUALIZER_DEFAULT:
            return { ...state, visualizerDefault: action.payload };
        case NOTES_SET_TAGS:
            return { ...state, tags: action.payload };
        case NOTES_ADD_TAG:
            return { ...state, tags: [...state.tags, action.payload] };
        case NOTES_REMOVE_TAG:
            return { ...state, tags: state.tags.filter((t) => t.id !== action.payload) };
        case NOTES_SET_ATTRIBUTES:
            return { ...state, attributes: action.payload };
        case NOTES_ADD_ATTRIBUTE:
            return { ...state, attributes: [...state.attributes, action.payload] };
        case NOTES_REPLACE_ATTRIBUTE:
            return { ...state, attributes: state.attributes.map((a) => (a.id === action.payload.id ? action.payload : a)) };
        case NOTES_REMOVE_ATTRIBUTE:
            return { ...state, attributes: state.attributes.filter((a) => a.id !== action.payload) };
        case INITIALIZE_STORE:
            return initialState;
        default:
            return state;
    }
};
